'use client';

import { useAuth0 } from '@auth0/auth0-react';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

import type { Auth0IdTokenClaims } from '@libs/common-next';
import {
    AUTH0_LOYALTY_ID_CLAIM,
    consumeAuth0LoginCompleted,
    hasPendingAuth0LoginCompleted,
    isAuth0CallbackPath,
    logSentryError,
    useDecisions,
    useLoyaltyContext,
    usePageLayout,
} from '@libs/common-next';

import { trackAuth0LoginSuccessful } from './useLoginEvents';

/**
 * Fires one `login` event after a completed Auth0 authentication returns to
 * PetSmart. Signup is also a successful authentication — `create_account` is
 * owned by `useAuth0CreateAccountEvent`.
 *
 * The completed-login signal is set only after token sync on `/auth/callback`
 * and is consumed here on the destination page so callback re-renders,
 * destination reloads, and existing Auth0 sessions do not send a duplicate.
 */
export function useAuth0LoginEvent() {
    const isAuth0Enabled = useDecisions('auth0');
    const { getIdTokenClaims, isAuthenticated, isLoading } = useAuth0();
    const { user } = usePageLayout();
    const { memberPoints } = useLoyaltyContext();
    const pathname = usePathname();

    const userRef = useRef(user);
    userRef.current = user;
    const memberPointsRef = useRef(memberPoints);
    memberPointsRef.current = memberPoints;

    useEffect(() => {
        if (
            !isAuth0Enabled ||
            isLoading ||
            !isAuthenticated ||
            isAuth0CallbackPath(pathname ?? '') ||
            !hasPendingAuth0LoginCompleted()
        ) {
            return;
        }

        let isMounted = true;

        async function trackCompletedAuth0Login() {
            let claims: Auth0IdTokenClaims | undefined;

            try {
                claims = (await getIdTokenClaims()) as Auth0IdTokenClaims | undefined;
            } catch (error) {
                logSentryError(error, {
                    tags: {
                        component: 'useAuth0LoginEvent',
                        action: 'getIdTokenClaims',
                        severity: 'LOW',
                    },
                });
            }

            if (!isMounted) {
                return;
            }

            const loginMethod = consumeAuth0LoginCompleted();
            if (!loginMethod) {
                return;
            }

            const email =
                typeof claims?.email === 'string' && claims.email ? claims.email : (userRef.current?.email ?? null);

            trackAuth0LoginSuccessful({
                loginMethod,
                email,
                loyaltyId: claims?.[AUTH0_LOYALTY_ID_CLAIM] ?? userRef.current?.loyaltyid,
                treatsPoints: memberPointsRef.current?.availablePoints,
                currentTierRank: memberPointsRef.current?.currentTierRank,
            });
        }

        void trackCompletedAuth0Login();

        return () => {
            isMounted = false;
        };
    }, [getIdTokenClaims, isAuth0Enabled, isAuthenticated, isLoading, pathname]);
}
